import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LuTrash } from "react-icons/lu";
import coursesInitial from "../courses";

const Course = ({ title, name, pholder, val, grade, act, onDelete }) => {
  const idBase = title.toLowerCase().replace(/\s+/g, "-");
  return (
    <div className="course-card">
      <div className="course-header">
        <span className="badge">{title}</span>
        <button type="button" className="icon-btn" onClick={onDelete} aria-label="Remove course">
          <LuTrash className="icon" />
        </button>
      </div>
      <div className="fields">
        <div className="field">
          <label htmlFor={`${idBase}-credits`}>Credits</label>
          <input
            id={`${idBase}-credits`}
            type="number"
            name={name}
            placeholder={pholder}
            value={val}
            onChange={act}
            min="0"
          />
          <div className="inline-hint">Enter credit value for this course.</div>
        </div>

        <div className="field">
          <label htmlFor={`${idBase}-grade`}>Expected Grade</label>
          <select id={`${idBase}-grade`} name="grade" value={grade} onChange={act}>
            <option value="">Select Grade</option>
            <option value="10">S Grade</option>
            <option value="9">A Grade</option>
            <option value="8">B Grade</option>
            <option value="7">C Grade</option>
            <option value="6">D Grade</option>
            <option value="5">E Grade</option>
          </select>
        </div>
      </div>
    </div>
  );
};

const GPAPredict = () => {
  const [courses, setCourses] = useState([]);
  const [gpa, setGpa] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [resultTone, setResultTone] = useState("success");
  const [resultNote, setResultNote] = useState("");

  // Load data from localStorage on component mount
  useEffect(() => {
    const savedCourses = JSON.parse(localStorage.getItem("courses"));
    if (savedCourses) setCourses(savedCourses);
    else setCourses(coursesInitial);
  }, []);

  // Save data to localStorage explicitly when "Save" is clicked
  const handleSave = () => {
    localStorage.setItem("courses", JSON.stringify(courses));
    setFeedback({ type: "success", message: "Courses saved locally for next visit." });
  };

  // Handle input changes
  const handleInputChange = (index, event) => {
    const { name, value } = event.target;
    setCourses((prevCourses) => {
      const updatedCourses = [...prevCourses];
      updatedCourses[index][name] = value;
      return updatedCourses;
    });
  };

  // Add a new course
  const handleAddCourse = () => {
    setCourses((prevCourses) => [
      ...prevCourses,
      {
        key: prevCourses.length + 1,
        title: `Course ${prevCourses.length + 1}`,
        name: "val",
        pholder: "Credits",
        val: "",
        grade: "",
      },
    ]);
  };

  // Delete a course
  const handleDeleteCourse = (index) => {
    setCourses((prevCourses) => prevCourses.filter((_, i) => i !== index));
    setIsCalculating(true);
  };

  // Reset input fields (but keep props intact)
  const handleReset = () => {
    setCourses((prevCourses) =>
      prevCourses.map((course) => ({
        ...course,
        val: "",
        grade: "",
      }))
    );
  };

  // Calculate GPA
  const handleCalculateGPA = () => {
    const totalCredits = courses.reduce((sum, course) => sum + (parseFloat(course.val) || 0),0);
    const weightedGrades = courses.reduce(
      (sum, course) =>
        sum + (parseFloat(course.val) || 0) * (parseFloat(course.grade) || 0),
      0
    );

    if (totalCredits === 0) {
      setGpa(0);
      setResultTone("warning");
      setResultNote("Add credits and grades to get a meaningful GPA.");
    } else {
      const calculated = (weightedGrades / totalCredits).toFixed(2);
      setGpa(calculated);

      const value = parseFloat(calculated);
      if (value >= 9) {
        setResultTone("success");
        setResultNote("Rock solid - great for VIT dream/TT placements. Keep this pace.");
      } else if (value >= 8) {
        setResultTone("warning");
        setResultNote("Good show - lift labs/theory a bit to cross 9.");
      } else if (value >= 7) {
        setResultTone("warning");
        setResultNote("Okay-ish - focus on heavy-credit slots to climb fast.");
      } else {
        setResultTone("danger");
        setResultNote("Low zone - target S/A in big-credit courses to bounce back.");
      }
    }

    setTimeout(() => setIsCalculating(false), 160);
  };

  const hasInvalidInputs = courses.some(
    (course) => (course.val && parseFloat(course.val) < 0) || (course.val && !course.grade)
  );

  const primaryLabel = isCalculating ? "Calculating..." : "Calculate GPA";

  return (
    <div className="app-shell">
      <main className="page">
        <div className="nav-actions">
          <Link to="/" className="btn btn-ghost">← Home</Link>
          <span className="pill">GPA Predictor</span>
        </div>

        <header className="page-header">
          <p className="eyebrow">Planner</p>
          <h1 className="page-title">Calculate GPA from credits &amp; grades</h1>
          <p className="lede">Keep your expected grades organized, save them locally, and see your projected GPA.</p>
        </header>

        <div className="card">
          <div className="card-heading">
            <div>
              <h2 className="card-title">Courses</h2>
              <p className="section-note">Enter credits and choose the grade you expect to earn.</p>
            </div>
            <button type="button" className="btn btn-secondary" onClick={handleAddCourse}>
              + Add course
            </button>
          </div>

          <div className="stack">
            {courses.map((course, index) => (
              <Course
                key={index}
                title={course.title}
                name="val"
                pholder={course.pholder}
                val={course.val}
                act={(e) => handleInputChange(index, e)}
                grade={course.grade}
                onDelete={() => handleDeleteCourse(index)}
              />
            ))}
          </div>

          <div className="controls">
            <button type="button" className="btn btn-secondary" onClick={handleSave}>
              Save
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleReset}>
              Reset
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleCalculateGPA}
              disabled={hasInvalidInputs || isCalculating}
            >
              {primaryLabel}
            </button>
          </div>
          <div className="inline-hint">Grades follow VIT absolute scale. Calculations stay on-device.</div>
          {feedback && <div className={`feedback ${feedback.type}`} aria-live="polite">{feedback.message}</div>}
        </div>

        {gpa !== null && (
          <div className={`result-card ${resultTone}`}>
            <p className="result-label">Projected GPA</p>
            <p className="result-value">{gpa}</p>
            <p className="muted">Computed using weighted credits and selected grades.</p>
            {resultNote && <p className="muted" style={{ marginTop: 6 }}>{resultNote}</p>}
          </div>
        )}
      </main>
    </div>
  );
};

export default GPAPredict;
